import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useEffect, useState } from "react";

// Simple className utility function
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

interface BreadcrumbItem {
  label: string;
  path: string;
  onClick?: (e: React.MouseEvent) => void;
}

// --- Optional extra trailing segment (for in-page views that aren't routes,
// e.g. the "Sell Tokens" flow inside the Secondary Marketplace page). ---
type BreadcrumbExtra = { label: string; onBack?: () => void } | null;
let currentExtra: BreadcrumbExtra = null;
const EXTRA_EVENT = "vestn:breadcrumb-extra";

export const setBreadcrumbExtra = (next: BreadcrumbExtra): void => {
  currentExtra = next;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(EXTRA_EVENT));
  }
};

// Route configuration for breadcrumbs - matches actual routes in the project
const routeConfig: Record<string, { label: string; parent?: string }> = {
  "/": { label: "Home" },
  "/platform": { label: "Platform", parent: "/" },
  "/primarymarketplace": { label: "Primary Marketplace", parent: "/" },
  "/marketplace": { label: "Marketplace", parent: "/" },
  "/secondarymarketplace": { label: "Secondary Marketplace", parent: "/" },
  "/secondary-marketplace": { label: "Secondary Marketplace", parent: "/" },
  "/secondary": { label: "Secondary Market", parent: "/" },
  "/rental": { label: "Yield", parent: "/" },
  "/user/profile": { label: "Portfolio", parent: "/" },
  "/profile": { label: "Profile", parent: "/user" },
  "/edit-profile": { label: "Edit Profile", parent: "/user/profile" },
  "/user": { label: "Dashboard", parent: "/" },
  "/dashboard": { label: "Dashboard", parent: "/" },
  "/settings": { label: "Settings", parent: "/user" },
  "/transactions": { label: "Transactions", parent: "/user" },
  "/collections": { label: "Collections", parent: "/" },
  "/collection-detail": { label: "Collection Details", parent: "/collections" },
  "/collection-edit": { label: "Edit Collection", parent: "/collections" },
  "/primaryPropertyDetails": {
    label: "Property Details",
    parent: "/primarymarketplace",
  },
  "/propertyDetails": {
    label: "Property Details",
    parent: "/secondarymarketplace",
  },
  "/rental/property": { label: "Rental Property", parent: "/rental" },
  "/creator": { label: "Creator Profile", parent: "/" },
  "/how-to-use": { label: "How It Works", parent: "/" },
  "/about": { label: "About Us", parent: "/" },
  "/legal": { label: "Legal", parent: "/" },
  "/faq": { label: "FAQ", parent: "/" },
  "/meet-the-team": { label: "Meet The Team", parent: "/about" },
  "/terms-and-conditions": { label: "Terms of Service", parent: "/" },
  "/terms": { label: "Terms of Service", parent: "/" },
  "/privacy": { label: "Privacy Policy", parent: "/" },
  "/cookies": { label: "Cookie Policy", parent: "/" },
  "/blog-list": { label: "Blog", parent: "/" },
  "/calculator": { label: "Calculator", parent: "/" },
};

const getBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  let currentPath = pathname;

  // Handle dynamic routes
  if (pathname.startsWith("/primaryPropertyDetails/")) {
    breadcrumbs.unshift({
      label: "Property Details",
      path: pathname,
    });
    currentPath = "/primarymarketplace";
  } else if (pathname.startsWith("/propertyDetails/")) {
    breadcrumbs.unshift({
      label: "Property Details",
      path: pathname,
    });
    currentPath = "/secondarymarketplace";
  } else if (pathname.startsWith("/rental/property/")) {
    breadcrumbs.unshift({
      label: "Rental Property",
      path: pathname,
    });
    currentPath = "/rental";
  } else if (pathname.startsWith("/collection-detail/")) {
    breadcrumbs.unshift({
      label: "Collection Details",
      path: pathname,
    });
    currentPath = "/collections";
  } else if (pathname.startsWith("/collection-edit/")) {
    breadcrumbs.unshift({
      label: "Edit Collection",
      path: pathname,
    });
    currentPath = "/collections";
  } else if (pathname.startsWith("/creator/")) {
    breadcrumbs.unshift({
      label: "Creator Profile",
      path: pathname,
    });
    currentPath = "/";
  } else if (pathname.startsWith("/user/")) {
    // Handle user routes
    if (pathname === "/user/profile" || pathname.startsWith("/user/profile")) {
      breadcrumbs.unshift({ label: "Portfolio", path: "/user/profile" });
      currentPath = "/";
    } else if (pathname.startsWith("/user/")) {
      breadcrumbs.unshift({ label: "Dashboard", path: "/user" });
      currentPath = "/";
    }
  }

  // Build breadcrumb chain
  while (currentPath && routeConfig[currentPath]) {
    const config = routeConfig[currentPath];
    breadcrumbs.unshift({ label: config.label, path: currentPath });
    currentPath = config.parent || "";
  }

  // Always add Home as first item if not already present
  if (breadcrumbs.length === 0 || breadcrumbs[0].path !== "/") {
    breadcrumbs.unshift({ label: "Home", path: "/platform" });
  }

  return breadcrumbs;
};

interface BreadcrumbsProps {
  className?: string;
}

export const Breadcrumbs = ({ className }: BreadcrumbsProps) => {
  const location = useLocation();
  const [extra, setExtra] = useState<BreadcrumbExtra>(currentExtra);

  useEffect(() => {
    const handler = () => setExtra(currentExtra);
    window.addEventListener(EXTRA_EVENT, handler);
    return () => window.removeEventListener(EXTRA_EVENT, handler);
  }, []);

  const baseBreadcrumbs = getBreadcrumbs(location.pathname);
  // Append the optional in-page segment (e.g. "Sell Tokens"). The previous last
  // item becomes clickable to go "back" to that view's base.
  const breadcrumbs: BreadcrumbItem[] = extra
    ? [
        ...baseBreadcrumbs.map((b, i) =>
          i === baseBreadcrumbs.length - 1 && extra.onBack
            ? {
                ...b,
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  extra.onBack?.();
                },
              }
            : b
        ),
        { label: extra.label, path: "__extra__" },
      ]
    : baseBreadcrumbs;

  const isPolicyPage =
    location.pathname === "/terms" ||
    location.pathname === "/privacy" ||
    location.pathname === "/cookies";

  // Don't show breadcrumbs on home, auth, or if only one item
  if (
    location.pathname === "/" ||
    location.pathname === "/platform" ||
    location.pathname === "/how-to-use" ||
    location.pathname === "/about" ||
    location.pathname === "/auth" ||
    location.pathname === "/onboarding" ||
    location.pathname.startsWith("/user/profile") ||
    breadcrumbs.length <= 1
  ) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        "container mx-auto flex w-full pr-4 lg:pr-8 items-center gap-1 mb-4 text-sm",
        isPolicyPage ? "pl-0" : "pl-6",
        className
      )}
      style={{
        color: "#8B949E",
      }}
    >
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const isFirst = index === 0;

        return (
          <div key={item.path} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight
                className="w-4 h-4"
                style={{
                  color: "rgba(139, 148, 158, 0.5)",
                }}
              />
            )}
            {isLast ? (
              <span
                className="font-medium truncate max-w-[200px]"
                style={{
                  color: "#FFFFFF",
                }}
              >
                {item.label}
              </span>
            ) : (
              <Link
                to={item.path}
                onClick={item.onClick}
                className={cn(
                  "flex items-center gap-1.5 transition-colors truncate max-w-[150px] hover:opacity-80",
                  isFirst && "gap-1"
                )}
                style={{
                  color: "#8B949E",
                }}
              >
                {isFirst && (
                  <Home
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{
                      color: "#8B949E",
                    }}
                  />
                )}
                <span>{item.label}</span>
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
};
